<script setup>
  import {computed, ref} from "vue";

  import ComponentIcon from "@components/Icons/Icon.vue";
  import PatientsPatientInformation from "@pages/Patients/Components/PatientInformation.vue";
  import {MedButton, MedTooltip} from "@pharma4u/medicheck-components";
  import {cloneDeep} from "es-toolkit";

  defineOptions({
    name: "ComponentSimpleExistingPatientSelect",
  });

  const props = defineProps({
    existingPatients: {
      required: true,
      type: Array,
    },
  });

  const emit = defineEmits(["selectedPatient", "patientSelected"]);

  const paginationItemsPerPage = 5;

  const currentPageNumber = ref(0);

  const pagesCount = computed(() => {
    return pages.value.length;
  });

  const currentPage = computed(() => {
    return pages.value[currentPageNumber.value] ?? [];
  });

  /** pagination items **/
  const pagination = computed(() => {
    const count = pages.value.length;

    let p = Array.from({length: count}, (_, i) => ({page: i, label: i + 1}));

    if (count < 5) {
      return p;
    }

    return p
      .map((pC, index) => {
        return index >= 2 &&
          index <= count - 3 &&
          (currentPageNumber.value - 1 > index || currentPageNumber.value + 1 < index)
          ? {page: null, label: "..."}
          : pC;
      })
      .filter((pC, index, pA) => (typeof pC.label === "number" ? true : typeof pA[index - 1].label === "number"));
  });

  const pages = computed(() => {
    // map through existing patients and format birthdate
    const patients = props.existingPatients.map((patient) => {
      const clonedPatient = cloneDeep(patient);
      const d = new Date(patient.birthdate ?? null);
      clonedPatient.birthdate = Number.isNaN(d.getTime()) ? null : d.toLocaleDateString("de-DE", {dateStyle: "medium"});
      return clonedPatient;
    });

    // slice up all patients into pages
    return Array.from({length: Math.ceil(patients.length / paginationItemsPerPage)}, (v, i) => {
      return patients.slice(i * paginationItemsPerPage, i * paginationItemsPerPage + paginationItemsPerPage);
    });
  });

  const setPage = (page) => {
    if (page !== null) {
      currentPageNumber.value = page;
    }
  };

  const prevPage = () => {
    if (currentPageNumber.value > 0) {
      currentPageNumber.value--;
    }
  };

  const nextPage = () => {
    if (currentPageNumber.value < pagesCount.value - 1) {
      currentPageNumber.value++;
    }
  };

  const select = (patient) => {
    emit("patientSelected", patient);
  };
</script>

<template>
  <table class="mt-3 w-full table-auto">
    <thead>
      <tr class="border-b-2 border-b-gray-200 leading-tight">
        <th class="px-0.5 py-2 text-left align-top font-normal">
          <div class="">Name, Vorname</div>
          <div class="text-xs">Geburtsdatum, Versichertennummer</div>
        </th>

        <th class="px-0.5 py-2 text-left align-top font-normal">
          <div>Information</div>
        </th>

        <th class="px-0.5 py-2 text-left align-top font-normal">
          <div>Letzte Änderung</div>
          <div class="text-xs">Bearbeiter</div>
        </th>

        <th class="px-0.5 py-2 text-right align-top font-normal" />
      </tr>
    </thead>

    <tbody>
      <tr
        v-for="patient in currentPage"
        :key="patient.id"
        class="border-t-2 border-gray-200 leading-tight hover:bg-gray-100"
      >
        <td class="px-0.5 py-2">
          <div class="text-lg font-semibold text-gray-700">{{ patient.lastname }}, {{ patient.firstname }}</div>

          <div v-if="patient.birthdate" class="text-xs">
            {{ patient.birthdate }}
            <span v-if="patient.insurancenumber">, {{ patient.insurancenumber }}</span>
          </div>

          <div v-else-if="patient.insurancenumber">{{ patient.insurancenumber }}</div>
        </td>

        <td class="px-0.5">
          <PatientsPatientInformation
            :patient="patient"
            :test-id="getTestId('patient-row-open-note')"
            target="_blank"
          />
        </td>

        <td class="px-0.5">
          {{ patient.updated_at }}

          <div v-if="patient.username" class="text-xs">
            {{ patient.username }}
          </div>
        </td>

        <td class="px-0.5">
          <div class="flex justify-end">
            <MedButton
              variant="primary"
              :data-test="getTestId('patient-row-add-to-existing-patient-button')"
              @click="select(patient)"
            >
              Auswählen
            </MedButton>
          </div>
        </td>
      </tr>

      <tr v-if="$slots?.newItem" class="border-t-2 border-gray-200 leading-tight hover:bg-gray-100">
        <td colspan="4" class="px-0.5 py-2">
          <slot name="newItem" />
        </td>
      </tr>
    </tbody>

    <tfoot v-if="pagesCount > 1">
      <tr>
        <td colspan="4" class="font-medium">
          <div class="flex items-start justify-between">
            <div class="flex flex-1">
              <button
                class="inline-flex items-center border-t-2 border-t-gray-200 py-4 pr-4 text-sm md:pl-4"
                :class="{
                  'hover:border-gray-600 hover:text-gray-600': currentPageNumber > 0,
                  'hover:cursor-default': currentPageNumber === 0,
                }"
                :data-test="getTestId('existing-patient-select-prev-page')"
                @click="prevPage"
              >
                <MedTooltip content="vorherige Seite">
                  <ComponentIcon>arrow_back_ios</ComponentIcon>
                </MedTooltip>

                <span class="px-2 md:hidden">vorherige Seite</span>
              </button>

              <div class="flex-1 border-t-2 border-t-gray-200" />
            </div>

            <div class="hidden md:flex">
              <button
                v-for="page in pagination"
                :key="page.page"
                class="inline-flex items-center border-t-2 border-t-gray-200 p-4 text-sm font-medium hover:no-underline"
                :class="{
                  'border-t-mcred text-mcred': page.page === currentPageNumber,
                  'hover:border-gray-600 hover:text-gray-700': page.page !== currentPageNumber && page.page !== null,
                  'hover:cursor-default': page.page === null,
                }"
                :data-test="getTestId('existing-patient-select-set-page')"
                @click="setPage(page.page)"
              >
                {{ page.label }}
              </button>
            </div>

            <div class="flex flex-1">
              <div class="flex-1 border-t-2 border-t-gray-200" />

              <button
                class="inline-flex items-center border-t-2 border-t-gray-200 py-4 pl-4 text-sm md:pr-4"
                :class="{
                  'hover:border-gray-600 hover:text-gray-600': currentPageNumber < pagesCount - 1,
                  'hover:cursor-default': currentPageNumber === pagesCount - 1,
                }"
                :data-test="getTestId('existing-patient-select-next-page')"
                @click="nextPage"
              >
                <span class="px-2 md:hidden">nächste Seite</span>

                <MedTooltip content="nächste Seite">
                  <ComponentIcon>arrow_forward_ios</ComponentIcon>
                </MedTooltip>
              </button>
            </div>
          </div>
        </td>
      </tr>
    </tfoot>
  </table>
</template>
